/**
 * Rax Create Agent Modal Component
 *
 * Modal for creating a new agent (band member).
 */

import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxTheme, raxButtonStyles, raxInputStyles } from "../styles/rax-theme.js";

export type AgentType = "work" | "personal" | "family" | "project" | "custom";
export type PersonaType = "researcher" | "designer" | "coder" | "writer" | "assistant";

export interface CreateAgentData {
  name: string;
  description: string;
  type: AgentType;
  persona?: PersonaType;
  icon: string;
  skills: string[];
}

interface AgentTemplate {
  id: string;
  label: string;
  summary: string;
  type: AgentType;
  icon: string;
  suggestedName: string;
  suggestedDescription: string;
  skills: string[];
}

const AGENT_TYPES: Array<{ type: AgentType; label: string; icon: string; description: string }> = [
  { type: "work", label: "Work", icon: "👔", description: "Email, Slack, calendar, tasks" },
  {
    type: "personal",
    label: "Personal",
    icon: "🏠",
    description: "Personal email, reminders, notes",
  },
  {
    type: "family",
    label: "Family",
    icon: "👨‍👩‍👧",
    description: "Shared calendars, groceries, coordination",
  },
  {
    type: "project",
    label: "Project",
    icon: "🚀",
    description: "Temporary agent for specific initiatives",
  },
  { type: "custom", label: "Custom", icon: "⚡", description: "Build your own from scratch" },
];

// Keep a single source of truth for available skills so defaults always track the full set.
const AVAILABLE_SKILLS = [
  { id: "memory", name: "Memory", icon: "🧠", desc: "Remember context" },
  { id: "web_search", name: "Web Search", icon: "🔍", desc: "Search the internet" },
  { id: "gmail", name: "Gmail", icon: "📧", desc: "Read and send email" },
  { id: "calendar", name: "Calendar", icon: "📅", desc: "View and manage events" },
  { id: "slack", name: "Slack", icon: "💬", desc: "Read and send workspace updates" },
  { id: "notion", name: "Notion", icon: "📝", desc: "Search and update workspace docs" },
  { id: "browser", name: "Browser", icon: "🌐", desc: "Browse websites" },
  { id: "code", name: "Code", icon: "💻", desc: "Write and run code" },
  { id: "files", name: "Files", icon: "📁", desc: "Read and write files" },
  { id: "terminal", name: "Terminal", icon: "⌨️", desc: "Run shell commands" },
];

// Default to all OpenClaw capabilities so users get the full experience without extra clicks.
const DEFAULT_SKILLS = AVAILABLE_SKILLS.map((skill) => skill.id);

const AGENT_TEMPLATES: AgentTemplate[] = [
  {
    id: "work-starter",
    label: "Work Starter",
    summary: "Inbox triage, calendar coordination, and Slack summaries.",
    type: "work",
    icon: "👔",
    suggestedName: "Work Copilot",
    suggestedDescription: "Manage work updates, meetings, and team follow-ups.",
    skills: ["memory", "gmail", "calendar", "slack", "web_search"],
  },
  {
    id: "research",
    label: "Research Analyst",
    summary: "Fast research loops with notes and source tracking.",
    type: "project",
    icon: "🔬",
    suggestedName: "Research Analyst",
    suggestedDescription: "Investigate topics, summarize findings, and draft notes.",
    skills: ["memory", "web_search", "notion", "browser", "files"],
  },
  {
    id: "family-coordinator",
    label: "Family Coordinator",
    summary: "Shared scheduling, reminders, and planning support.",
    type: "family",
    icon: "👨‍👩‍👧",
    suggestedName: "Family Planner",
    suggestedDescription: "Track shared plans, reminders, and household coordination.",
    skills: ["memory", "calendar", "gmail"],
  },
];

/**
 * Persona templates for specialized agent roles
 */
interface PersonaTemplate {
  id: PersonaType;
  name: string;
  description: string;
  icon: string;
  defaultSkills: string[];
  suggestedType: AgentType;
  /** Visual palette color (primary shirt color) */
  color: string;
}

const PERSONA_TEMPLATES: PersonaTemplate[] = [
  {
    id: "researcher",
    name: "Researcher",
    description: "Expert at finding information, analyzing data, and summarizing findings",
    icon: "🔬",
    defaultSkills: ["memory", "web_search", "browser", "notion", "files"],
    suggestedType: "project",
    color: "#4488CC",
  },
  {
    id: "designer",
    name: "Designer",
    description: "Focused on visual tasks, UI/UX suggestions, and creative direction",
    icon: "🎨",
    defaultSkills: ["memory", "web_search", "browser", "files", "code"],
    suggestedType: "project",
    color: "#AA55CC",
  },
  {
    id: "coder",
    name: "Coder",
    description: "Expert at code generation, debugging, and software architecture",
    icon: "💻",
    defaultSkills: ["memory", "code", "terminal", "files", "web_search", "browser"],
    suggestedType: "work",
    color: "#44AA66",
  },
  {
    id: "writer",
    name: "Writer",
    description: "Focused on content creation, editing, and communication",
    icon: "✏️",
    defaultSkills: ["memory", "web_search", "notion", "gmail", "files"],
    suggestedType: "work",
    color: "#FF8844",
  },
  {
    id: "assistant",
    name: "Assistant",
    description: "General purpose helper for everyday tasks and coordination",
    icon: "🤖",
    defaultSkills: ["memory", "web_search", "gmail", "calendar", "slack", "notion", "browser"],
    suggestedType: "personal",
    color: "#CCAA33",
  },
];

@customElement("rax-create-agent-modal")
export class RaxCreateAgentModal extends LitElement {
  static override styles = [
    raxTheme,
    raxButtonStyles,
    raxInputStyles,
    css`
      :host {
        display: block;
      }
      
      .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        padding: var(--space-5);
        backdrop-filter: blur(4px);
      }
      
      .modal {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-xl);
        width: 100%;
        max-width: 520px;
        max-height: 90vh;
        overflow-y: auto;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
      }
      
      .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--space-5) var(--space-6);
        border-bottom: 1px solid var(--border-color);
      }
      
      .modal-title {
        font-size: var(--font-xl);
        font-weight: 700;
        margin: 0;
        color: var(--text-primary);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .modal-title .icon {
        color: var(--rax-yellow);
      }
      
      .close-btn {
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: var(--text-tertiary);
        padding: var(--space-1);
        line-height: 1;
        transition: color var(--transition-fast);
      }
      
      .close-btn:hover {
        color: var(--text-primary);
      }
      
      .modal-body {
        padding: var(--space-6);
      }
      
      .step-indicator {
        display: flex;
        gap: var(--space-2);
        margin-bottom: var(--space-6);
        justify-content: center;
      }
      
      .step-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: var(--border-color);
        transition: all var(--transition-fast);
      }
      
      .step-dot.active {
        background: var(--rax-yellow);
        box-shadow: 0 0 12px var(--accent-glow);
      }
      
      .step-dot.completed {
        background: var(--success);
      }
      
      /* Step 1: Type Selection */
      .type-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: var(--space-3);
      }
      
      .type-option {
        padding: var(--space-4);
        border: 2px solid var(--border-color);
        border-radius: var(--radius-lg);
        cursor: pointer;
        transition: all var(--transition-fast);
        text-align: center;
        background: var(--bg-elevated);
      }
      
      .type-option:hover {
        border-color: var(--rax-yellow);
      }
      
      .type-option.selected {
        border-color: var(--rax-yellow);
        background: var(--accent-glow);
        box-shadow: 0 0 20px var(--accent-glow);
      }
      
      .type-icon {
        font-size: 36px;
        margin-bottom: var(--space-2);
      }
      
      .type-label {
        font-weight: 600;
        font-size: var(--font-sm);
        margin-bottom: var(--space-1);
        color: var(--text-primary);
      }
      
      .type-description {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        line-height: 1.4;
      }
      
      /* Step 2: Details */
      .form-group {
        margin-bottom: var(--space-5);
      }
      
      .form-row {
        display: flex;
        gap: var(--space-4);
      }
      
      .form-row .form-group {
        flex: 1;
      }
      
      .icon-picker {
        display: flex;
        gap: var(--space-2);
        flex-wrap: wrap;
      }
      
      .icon-option {
        width: 44px;
        height: 44px;
        border: 2px solid var(--border-color);
        border-radius: var(--radius-md);
        background: var(--bg-elevated);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .icon-option:hover {
        border-color: var(--rax-yellow);
      }
      
      .icon-option.selected {
        border-color: var(--rax-yellow);
        background: var(--accent-glow);
      }
      
      textarea.input {
        min-height: 80px;
        resize: vertical;
      }
      
      /* Step 3: Skills */
      .skills-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: var(--space-3);
      }
      
      .skill-option {
        padding: var(--space-3) var(--space-4);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: var(--space-3);
        transition: all var(--transition-fast);
        background: var(--bg-elevated);
      }
      
      .skill-option:hover {
        border-color: var(--text-tertiary);
      }
      
      .skill-option.selected {
        border-color: var(--rax-yellow);
        background: var(--accent-glow);
      }
      
      .skill-checkbox {
        width: 20px;
        height: 20px;
        border: 2px solid var(--border-color);
        border-radius: var(--radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: all var(--transition-fast);
      }
      
      .skill-option.selected .skill-checkbox {
        background: var(--rax-yellow);
        border-color: var(--rax-yellow);
        color: var(--rax-black);
      }
      
      .skill-icon {
        font-size: 20px;
      }
      
      .skill-details {
        flex: 1;
        min-width: 0;
      }
      
      .skill-name {
        font-weight: 500;
        font-size: var(--font-sm);
        color: var(--text-primary);
      }
      
      .skill-desc {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }
      
      /* Footer */
      .modal-footer {
        display: flex;
        justify-content: space-between;
        padding: var(--space-5) var(--space-6);
        border-top: 1px solid var(--border-color);
        background: var(--bg-elevated);
      }
      
      /* Step content titles */
      .step-title {
        font-size: var(--font-lg);
        font-weight: 600;
        margin-bottom: var(--space-2);
        color: var(--text-primary);
      }
      
      .step-subtitle {
        font-size: var(--font-sm);
        color: var(--text-secondary);
        margin-bottom: var(--space-5);
      }
      
      .template-grid {
        display: grid;
        gap: var(--space-3);
        margin-bottom: var(--space-5);
      }
      
      .template-card {
        border: 1px solid var(--border-color);
        background: var(--bg-elevated);
        border-radius: var(--radius-md);
        padding: var(--space-3);
        cursor: pointer;
        transition:
          border-color var(--transition-fast),
          box-shadow var(--transition-fast);
      }
      
      .template-card:hover {
        border-color: var(--rax-yellow);
      }
      
      .template-card.selected {
        border-color: var(--rax-yellow);
        box-shadow: 0 0 16px var(--accent-glow);
      }
      
      .template-title {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        color: var(--text-primary);
        font-weight: 600;
        margin-bottom: var(--space-1);
      }
      
      .template-summary {
        font-size: var(--font-xs);
        color: var(--text-secondary);
      }
      
      /* Persona selection styles */
      .persona-grid {
        display: grid;
        gap: var(--space-3);
        margin-bottom: var(--space-4);
      }
      
      .persona-card {
        border: 1px solid var(--border-color);
        background: var(--bg-elevated);
        border-radius: var(--radius-md);
        padding: var(--space-4);
        cursor: pointer;
        transition:
          border-color var(--transition-fast),
          box-shadow var(--transition-fast);
      }
      
      .persona-card:hover {
        border-color: var(--rax-yellow);
      }
      
      .persona-card.selected {
        border-color: var(--rax-yellow);
        box-shadow: 0 0 16px var(--accent-glow);
      }
      
      .persona-header {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin-bottom: var(--space-2);
      }
      
      .persona-icon {
        font-size: 24px;
      }
      
      .persona-name {
        font-weight: 600;
        color: var(--text-primary);
        flex: 1;
      }
      
      .persona-color {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        border: 2px solid rgba(255, 255, 255, 0.2);
      }
      
      .persona-description {
        font-size: var(--font-xs);
        color: var(--text-secondary);
        line-height: 1.4;
      }
      
      .skip-persona {
        width: 100%;
        margin-top: var(--space-2);
        color: var(--text-tertiary);
        font-size: var(--font-sm);
      }
      
      .skip-persona:hover {
        color: var(--text-secondary);
      }
      
      .btn-ghost {
        background: transparent;
        border: 1px dashed var(--border-color);
        color: var(--text-tertiary);
        cursor: pointer;
        padding: var(--space-3);
        border-radius: var(--radius-md);
        transition: all var(--transition-fast);
      }
      
      .btn-ghost:hover {
        border-color: var(--text-tertiary);
        color: var(--text-secondary);
      }
    `,
  ];

  @property({ type: Boolean })
  open = false;

  @state()
  private step = 1;

  @state()
  private selectedType: AgentType | null = null;

  @state()
  private selectedTemplateId: string | null = null;

  @state()
  private selectedPersona: PersonaType | null = null;

  @state()
  private name = "";

  @state()
  private description = "";

  @state()
  private selectedIcon = "⚡";

  @state()
  private selectedSkills: string[] = [...DEFAULT_SKILLS];

  private readonly availableIcons = [
    "⚡",
    "🎸",
    "🎯",
    "📊",
    "💼",
    "🏠",
    "👨‍👩‍👧",
    "🚀",
    "🤖",
    "🧠",
    "📧",
    "📅",
    "🔍",
    "💡",
    "🎨",
    "📝",
  ];

  private readonly availableSkills = AVAILABLE_SKILLS;

  override render() {
    if (!this.open) return null;

    return html`
      <div class="modal-overlay" @click=${this.onOverlayClick}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <h2 class="modal-title">
              <span class="icon">⚡</span>
              Add to Your Band
            </h2>
            <button class="close-btn" @click=${this.onClose}>×</button>
          </div>

          <div class="modal-body">
            <div class="step-indicator">
              ${[1, 2, 3, 4].map(
                (s) => html`
                <div class="step-dot ${s === this.step ? "active" : ""} ${s < this.step ? "completed" : ""}"></div>
              `,
              )}
            </div>

            ${this.step === 1 ? this.renderStep1() : ""}
            ${this.step === 2 ? this.renderStep2Persona() : ""}
            ${this.step === 3 ? this.renderStep3Details() : ""}
            ${this.step === 4 ? this.renderStep4Skills() : ""}
          </div>

          <div class="modal-footer">
            <button class="btn btn-secondary" @click=${this.onBack}>
              ${this.step === 1 ? "Cancel" : "Back"}
            </button>
            <button class="btn btn-primary" @click=${this.onNext} ?disabled=${!this.canProceed()}>
              ${this.step === 4 ? "⚡ Create Agent" : "Next"}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderStep1() {
    return html`
      <div class="step-title">What kind of agent?</div>
      <div class="step-subtitle">Choose a type that fits your needs.</div>
      <div class="template-grid">
        ${AGENT_TEMPLATES.map(
          (template) => html`
          <div
            class="template-card ${this.selectedTemplateId === template.id ? "selected" : ""}"
            @click=${() => this.applyTemplate(template)}
          >
            <div class="template-title">
              <span>${template.icon}</span>
              <span>${template.label}</span>
            </div>
            <div class="template-summary">${template.summary}</div>
          </div>
        `,
        )}
      </div>
      <div class="type-grid">
        ${AGENT_TYPES.map(
          (t) => html`
          <div
            class="type-option ${this.selectedType === t.type ? "selected" : ""}"
            @click=${() => {
              this.selectedType = t.type;
              this.selectedTemplateId = null;
            }}
          >
            <div class="type-icon">${t.icon}</div>
            <div class="type-label">${t.label}</div>
            <div class="type-description">${t.description}</div>
          </div>
        `,
        )}
      </div>
    `;
  }

  private renderStep2Persona() {
    return html`
      <div class="step-title">Choose a persona</div>
      <div class="step-subtitle">Select a specialized role for your agent, or skip for a custom setup.</div>

      <div class="persona-grid">
        ${PERSONA_TEMPLATES.map(
          (persona) => html`
          <div
            class="persona-card ${this.selectedPersona === persona.id ? "selected" : ""}"
            @click=${() => this.applyPersona(persona)}
          >
            <div class="persona-header">
              <span class="persona-icon">${persona.icon}</span>
              <span class="persona-name">${persona.name}</span>
              <span class="persona-color" style="background: ${persona.color}"></span>
            </div>
            <div class="persona-description">${persona.description}</div>
          </div>
        `,
        )}
      </div>

      <button class="btn btn-ghost skip-persona" @click=${() => this.skipPersona()}>
        Skip - I'll customize from scratch
      </button>
    `;
  }

  private renderStep3Details() {
    return html`
      <div class="step-title">Name your agent</div>
      <div class="step-subtitle">Give it a name and personality.</div>

      <div class="form-row">
        <div class="form-group">
          <label class="input-label">Name</label>
          <input
            class="input"
            type="text"
            placeholder="e.g., Work Assistant"
            .value=${this.name}
            @input=${(e: InputEvent) => (this.name = (e.target as HTMLInputElement).value)}
          />
        </div>
      </div>

      <div class="form-group">
        <label class="input-label">Icon</label>
        <div class="icon-picker">
          ${this.availableIcons.map(
            (icon) => html`
            <div
              class="icon-option ${this.selectedIcon === icon ? "selected" : ""}"
              @click=${() => (this.selectedIcon = icon)}
            >${icon}</div>
          `,
          )}
        </div>
      </div>

      <div class="form-group">
        <label class="input-label">Description (optional)</label>
        <textarea
          class="input"
          placeholder="What should this agent help you with?"
          .value=${this.description}
          @input=${(e: InputEvent) => (this.description = (e.target as HTMLTextAreaElement).value)}
        ></textarea>
      </div>
    `;
  }

  private renderStep4Skills() {
    return html`
      <div class="step-title">Choose skills</div>
      <div class="step-subtitle">What should your agent be able to do?</div>

      <div class="skills-grid">
        ${this.availableSkills.map(
          (skill) => html`
          <div
            class="skill-option ${this.selectedSkills.includes(skill.id) ? "selected" : ""}"
            @click=${() => this.toggleSkill(skill.id)}
          >
            <div class="skill-checkbox">
              ${this.selectedSkills.includes(skill.id) ? "✓" : ""}
            </div>
            <div class="skill-icon">${skill.icon}</div>
            <div class="skill-details">
              <div class="skill-name">${skill.name}</div>
              <div class="skill-desc">${skill.desc}</div>
            </div>
          </div>
        `,
        )}
      </div>
    `;
  }

  private toggleSkill(skillId: string) {
    if (this.selectedSkills.includes(skillId)) {
      this.selectedSkills = this.selectedSkills.filter((s) => s !== skillId);
    } else {
      this.selectedSkills = [...this.selectedSkills, skillId];
    }
  }

  private applyTemplate(template: AgentTemplate) {
    this.selectedTemplateId = template.id;
    this.selectedType = template.type;
    this.selectedIcon = template.icon;
    this.name = template.suggestedName;
    this.description = template.suggestedDescription;
    this.selectedSkills = [...template.skills];
    this.step = 2;
  }

  private applyPersona(persona: PersonaTemplate) {
    this.selectedPersona = persona.id;
    this.selectedIcon = persona.icon;
    this.name = persona.name;
    this.description = persona.description;
    this.selectedSkills = [...persona.defaultSkills];
    if (!this.selectedType) {
      this.selectedType = persona.suggestedType;
    }
    this.step = 3;
  }

  private skipPersona() {
    this.selectedPersona = null;
    this.step = 3;
  }

  private canProceed(): boolean {
    switch (this.step) {
      case 1:
        return this.selectedType !== null;
      case 2:
        return true; // Persona is optional, can always proceed
      case 3:
        return this.name.trim().length > 0;
      case 4:
        return this.selectedSkills.length > 0;
      default:
        return false;
    }
  }

  private onNext() {
    if (!this.canProceed()) return;

    if (this.step < 4) {
      this.step++;
    } else {
      this.onCreate();
    }
  }

  private onBack() {
    if (this.step > 1) {
      this.step--;
    } else {
      this.onClose();
    }
  }

  private onCreate() {
    const data: CreateAgentData = {
      name: this.name,
      description: this.description,
      type: this.selectedType!,
      persona: this.selectedPersona ?? undefined,
      icon: this.selectedIcon,
      skills: this.selectedSkills,
    };

    this.dispatchEvent(
      new CustomEvent("create-agent", {
        bubbles: true,
        detail: data,
      }),
    );

    this.reset();
  }

  private onClose() {
    this.dispatchEvent(new CustomEvent("close", { bubbles: true }));
    this.reset();
  }

  private onOverlayClick() {
    this.onClose();
  }

  private reset() {
    this.step = 1;
    this.selectedType = null;
    this.selectedTemplateId = null;
    this.selectedPersona = null;
    this.name = "";
    this.description = "";
    this.selectedIcon = "⚡";
    this.selectedSkills = [...DEFAULT_SKILLS];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-create-agent-modal": RaxCreateAgentModal;
  }
}
